import { ComponentPropsWithoutRef, MouseEvent, ReactNode } from "react";
import { Body } from "../body/Body";
import { Checkbox } from "../checkbox/Checkbox";
import { CheckboxSkeleton } from "../checkbox/Checkbox.Skeleton";
import { Ellipsis } from "../ellipsis/Ellipsis";
import {
  CHECKBOX_COLUMN_WIDTH,
  Container,
  Props,
  ScrollWrapper,
  SortDirection,
  TableBody,
  TableCell,
  TableColumn,
  TableData,
  TableHead,
  TableHeadRow,
  TableHeaderCell,
  TableHeaderCellContent,
  TableRow,
  columnToKey,
  defaultGetRowProps,
  handleKeyDown,
  isCustomTableColumn,
  text,
} from "./Table.Parts";
import { SortIcon } from "./sort-icon/SortIcon";

export const Table = <
  DataType extends TableData,
  CustomColumnKeys extends string = string,
>({
  data,
  columns,
  onSortChange,
  sortState,
  onRowClick,
  getRowProps = defaultGetRowProps,
  hideHeader = false,
  scrollableMaxHeight,
  selectedIds = [],
  onSelectedIdsChange,
  selectionSkeleton = false,
  ...tableProps
}: Props<DataType, CustomColumnKeys> & ComponentPropsWithoutRef<"table">) => {
  const selectable = Boolean(onSelectedIdsChange) || selectionSkeleton;

  const allIds = data.map((row) => row.id);
  const selectedIdSet = new Set<DataType["id"]>(selectedIds);

  const allSelected =
    allIds.length > 0 && allIds.every((id) => selectedIdSet.has(id));
  const someSelected = allIds.some((id) => selectedIdSet.has(id));

  const toggleAll = () => onSelectedIdsChange?.(allSelected ? [] : allIds);

  const toggleRow = (id: DataType["id"]) =>
    onSelectedIdsChange?.(
      selectedIdSet.has(id)
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id],
    );

  return (
    <ScrollWrapper $maxHeight={scrollableMaxHeight}>
      <Container {...tableProps}>
        {hideHeader ? null : (
          <TableHead $sticky={Boolean(scrollableMaxHeight)}>
            <TableHeadRow>
              {selectable && (
                <TableHeaderCell $width={CHECKBOX_COLUMN_WIDTH}>
                  {selectionSkeleton ? (
                    <CheckboxSkeleton />
                  ) : (
                    <Checkbox
                      role="checkbox"
                      aria-label={text.selectAllAriaLabel}
                      aria-checked={
                        allSelected ? true : someSelected ? "mixed" : false
                      }
                      variant={
                        allSelected
                          ? "checked"
                          : someSelected
                            ? "indeterminate"
                            : "unchecked"
                      }
                      onClick={toggleAll}
                    />
                  )}
                </TableHeaderCell>
              )}
              {columns.map((column) => (
                <HeaderCell
                  key={columnToKey(column)}
                  column={column}
                  sortState={sortState}
                  onSortChange={onSortChange}
                />
              ))}
            </TableHeadRow>
          </TableHead>
        )}
        <TableBody>
          {data.map((row) => (
            <TableRow
              key={row.id}
              {...getRowProps(
                {
                  tabIndex: onRowClick ? 0 : undefined,
                  onClick: onRowClick ? () => onRowClick(row) : undefined,
                  $clickable: Boolean(onRowClick),
                  onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) =>
                    handleKeyDown(
                      e,
                      onRowClick ? () => onRowClick(row) : undefined,
                    ),
                },
                row,
              )}
            >
              {selectable && (
                <TableCell $width={CHECKBOX_COLUMN_WIDTH}>
                  {selectionSkeleton ? (
                    <CheckboxSkeleton />
                  ) : (
                    <Checkbox
                      aria-label={text.selectAriaLabel}
                      variant={
                        selectedIds.includes(row.id) ? "checked" : "unchecked"
                      }
                      onClick={(e: MouseEvent<HTMLDivElement>) => {
                        // Don't trigger the row's onRowClick when toggling selection.
                        e.stopPropagation();
                        toggleRow(row.id);
                      }}
                    />
                  )}
                </TableCell>
              )}
              {columns.map((column) => (
                <BodyCell key={columnToKey(column)} column={column} row={row} />
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Container>
    </ScrollWrapper>
  );
};

const BodyCell = <
  DataType extends TableData,
  CustomColumnKeys extends string = string,
>({
  column,
  row,
}: {
  column: TableColumn<DataType, CustomColumnKeys>;
  row: DataType;
}) => {
  let children: ReactNode = null;
  if (isCustomTableColumn(column) || column.render) {
    children = column.render?.(row);
  } else {
    children = (
      <Ellipsis>
        <Body>{String(row[column.dataKey])}</Body>
      </Ellipsis>
    );
  }

  return (
    <TableCell $showMediaQuery={column.showMediaQuery} $width={column.width}>
      {children}
    </TableCell>
  );
};

const HeaderCell = <
  DataType extends TableData,
  CustomColumnKeys extends string = string,
>({
  column,
  onSortChange,
  sortState,
}: {
  column: TableColumn<DataType, CustomColumnKeys>;
} & Pick<Props<DataType, CustomColumnKeys>, "onSortChange" | "sortState">) => {
  const sorts = isCustomTableColumn(column)
    ? column.sorts
    : column.sortable && column.dataKey;
  const sortable = Boolean(sorts);

  const sortChange =
    onSortChange && sorts
      ? () =>
          onSortChange({
            direction: newSortDirection,
            key: sorts,
          })
      : undefined;

  const currentSortKey = sortState?.key;
  let newSortDirection: SortDirection = SortDirection.ASCENDING;
  if (sortState && currentSortKey === sorts) {
    switch (sortState.direction) {
      case SortDirection.ASCENDING:
        newSortDirection = SortDirection.DESCENDING;
        break;
      case SortDirection.DESCENDING:
        newSortDirection = SortDirection.NONE;
        break;
      case SortDirection.NONE:
        newSortDirection = SortDirection.ASCENDING;
        break;
    }
  }

  return (
    <TableHeaderCell
      $active={
        sortState &&
        currentSortKey === sorts &&
        (sortState.direction === SortDirection.ASCENDING ||
          sortState.direction === SortDirection.DESCENDING)
      }
      $sortable={sortable}
      tabIndex={sortable ? 0 : undefined}
      $showMediaQuery={column.showMediaQuery}
      $width={column.width}
      onClick={sortChange}
      onKeyDown={(e: React.KeyboardEvent<HTMLTableCellElement>) =>
        handleKeyDown(e, sortChange)
      }
    >
      <TableHeaderCellContent>
        {typeof column.label === "string" ? (
          <Ellipsis>
            <Body variant="small-strong">{column.label}</Body>
          </Ellipsis>
        ) : (
          column.label
        )}
        {sortable && onSortChange && (
          <SortIcon
            $sortDirection={
              sortState && currentSortKey === sorts
                ? sortState.direction
                : SortDirection.NONE
            }
          />
        )}
      </TableHeaderCellContent>
    </TableHeaderCell>
  );
};
